import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import {
  IConfiguratorAggContextLeaseRequest,
  IConfiguratorPart,
  IQuoteItemFormGroup,
} from '../quotes/shared/interfaces';
import { Subject, Subscription } from 'rxjs';
import { IActiveAggregate } from '../configurator/interfaces';
import { ConfiguratorPartSelection } from './types';
import { IPartWaybill } from './interfaces';
import { PartsClearanceProcessorService } from './parts-clearance-processor.service';
import { ItemType } from '../quotes/shared/models';
import { NotificationBrokerService } from '../../core/notification-broker.service';
import { ContextAction } from '../../core/interfaces';

@Injectable()
export class ConfiguratorService {
  private notificationBus = inject(NotificationBrokerService);
  private selected_part: Subject<ConfiguratorPartSelection> =
    new Subject<ConfiguratorPartSelection>();
  public selected_part$ = this.selected_part.asObservable();

  private onPaste: Subject<void> = new Subject<void>();
  public onPaste$ = this.onPaste.asObservable();

  private _isBaseContext: WritableSignal<boolean> = signal(true);
  public isBaseContext = this._isBaseContext.asReadonly();

  constructor() {
    this.notificationBus.on().subscribe((notification) => {
      if (notification.type == ContextAction.TERMINATED) {
        const item = notification.payload;
        const id = item.internal_id || item.context_id;
        this.relinquishContext(id);
      }

      if (notification.type == ContextAction.CREATED) {
        const payload = notification.payload;
        const controls = notification.payload.item
          .controls as unknown as IQuoteItemFormGroup;
        const description = controls.quote_item_description;

        if (payload.item) {
          const lease: IConfiguratorAggContextLeaseRequest = {
            internal_id: payload.context_id,
            aggregate_type: payload.context_type,
            title: description?.value,
            subscription: description?.valueChanges,
          };

          this.pushAggregate(lease);
        }
      }
    });
  }

  public activeAggregates: IActiveAggregate[] = [];
  private description_subscriptions: any[] = [];

  private current_waybill_subscription: Subscription | null = null;
  private current_waybill: IPartWaybill | null = null;

  set_clearance_processor(processor: PartsClearanceProcessorService) {
    this.current_waybill_subscription = processor.current_waybill$.subscribe(
      (new_waybill: IPartWaybill) => {
        this.update_configurator_state(new_waybill);
      },
    );
  }

  canAddPart(part: IConfiguratorPart) {
    let part_type: ItemType = part.type;
    if (part.part_is_enclosure) {
      part_type = ItemType.ENCLOSURE;
    }

    if (
      this.current_waybill &&
      this.current_waybill.allowed_part_types.indexOf(part_type) >= 0
    ) {
      return true;
    }

    return false;
  }

  canAddPartType(part_type: ItemType) {
    if (
      this.current_waybill &&
      this.current_waybill.allowed_part_types.indexOf(part_type) >= 0
    ) {
      return true;
    }

    return false;
  }

  update_configurator_state(waybill: IPartWaybill) {
    this.current_waybill = waybill;
    this._isBaseContext.set(waybill.isBaseContext);
  }

  pasteItem(): void {
    this.onPaste.next();
  }

  destroy_context() {
    if (this.current_waybill_subscription) {
      this.current_waybill_subscription.unsubscribe();
    }
  }

  set_selected_part(part: Partial<IConfiguratorPart>) {
    this.selected_part.next(part);
  }

  //@TODO: this function's name is ambiguous, the configurator service
  //       does indeed need to manage the aggregate context stack.
  //       Aggregate context stack is used to determine which aggregate
  //       to display in the side bar.
  pushAggregate(newAggregate: IConfiguratorAggContextLeaseRequest | null) {
    if (!newAggregate?.internal_id) {
      return;
    }

    if (newAggregate) {
      const currentAggregate = this.peekAggregateContext();
      if (currentAggregate?.internal_id == newAggregate.internal_id) {
        return;
      }

      const newActiveAggregate: IActiveAggregate = {
        internal_id: newAggregate.internal_id,
        type: this.capitalise(newAggregate.aggregate_type),
        isActive: true,
      };

      if (this.aggregateExists(newAggregate.internal_id)) {
        let activeAggregate = this.peekAggregateContext();
        while (activeAggregate?.internal_id != newAggregate.internal_id) {
          this.popAggregate();
          activeAggregate = this.peekAggregateContext();
        }

        return;
      }

      this.disableOtherAggregateContexts();
      if (newAggregate.relinquish) {
        this.relinquishContext(newAggregate.internal_id);
        return;
      }

      if (newAggregate.subscription) {
        const sub = newAggregate.subscription.subscribe((newval) => {
          const sub = this.description_subscriptions.find(
            (x) => x.id == newAggregate.internal_id,
          );
          if (newval) {
            sub.desc = newval;
          }
        });

        this.description_subscriptions.push({
          id: newAggregate.internal_id,
          desc: newAggregate.title || newActiveAggregate.type,
          sub: sub,
        });
      }

      this.activeAggregates.push(newActiveAggregate);
    }
  }

  popAggregate() {
    const removed = this.activeAggregates.pop();
    const sub = this.description_subscriptions.find(
      (x) => x.id == removed?.internal_id,
    );
    if (sub) {
      sub.sub.unsubscribe();
      this.description_subscriptions = this.description_subscriptions.filter(
        (x) => x.id != removed?.internal_id,
      );
    }

    this.updateActiveAggregate();
    if (this.notificationBus) {
      const notification = {
        type: ContextAction.TERMINATED,
        payload: removed,
      };
      this.notificationBus.emit(notification);
    }

    return removed;
  }

  updateActiveAggregate() {
    const currentActiveAgg = this.peekAggregateContext();
    if (currentActiveAgg) {
      currentActiveAgg.isActive = true;
    }
  }

  aggregateExists(id: number): boolean {
    for (const item of this.activeAggregates) {
      if (item.internal_id == id) {
        return true;
      }
    }

    return false;
  }

  relinquishContext(id: number) {
    this.activeAggregates = this.activeAggregates.filter((x) => {
      return x.internal_id != id;
    });
    this.updateActiveAggregate();
  }

  resetAggregateContext() {
    this.activeAggregates = [];
  }

  peekAggregateContext(): IActiveAggregate | undefined {
    if (this.activeAggregates.length) {
      return this.activeAggregates[this.activeAggregates.length - 1];
    }

    return undefined;
  }

  getAggregateDescription(id: number): string | null {
    const desc = this.description_subscriptions.find((x) => x.id == id);
    if (desc) {
      return desc.desc;
    }

    return null;
  }

  disableOtherAggregateContexts() {
    this.activeAggregates.map((agg) => {
      agg.isActive = false;
    });
  }

  isAggregateContextEmpty(): boolean {
    if (this.activeAggregates.length) {
      return false;
    }

    return true;
  }

  capitalise(name: string) {
    if (!name) {
      return 'None';
    }

    if (name && name == '') {
      return 'None';
    }

    let nameparts: string[] = name.split('_');
    nameparts = nameparts.map((x) => {
      return x[0].toUpperCase() + x.slice(1);
    });

    return nameparts.join(' ');
  }
}
